import { useState } from 'react'

import { useLingui } from '@lingui/react'
import { useFormContext } from 'react-hook-form'
import type { ControllerProps } from 'react-hook-form'

import type { I18nLanguage } from '@twistopayments/design-system/base'
import { useDesignSystem } from '@twistopayments/design-system/base'
import {
  formatCurrencyNumber,
  parseFormattedCurrencyNumber,
} from '@twistopayments/design-system/utils'

import { CurrencyInputBase } from '../../atoms/currency-input-base'
import { FormHelperText } from '../../atoms/form-helper-text'
import { InputLabel } from '../../atoms/input-label'

import { CurrencySign, InputBox } from './components'
import * as styles from './currency-input.styles'

type Props = {
  ariaLabel: string
  autoFocus?: boolean
  disabled?: boolean
  readOnly?: boolean
  id?: string
  label?: string
  name: string
  max?: number
  min?: number
  currencyCode?: string
  rules?: ControllerProps['rules']
  textAlign?: 'start' | 'end'
}

export const CurrencyInput = ({
  ariaLabel,
  autoFocus = false,
  disabled = false,
  readOnly = false,
  id: nodeId,
  label,
  name,
  max,
  min,
  currencyCode,
  rules,
  textAlign = 'end',
}: Props) => {
  const [focused, setFocused] = useState(false)
  const { watch, formState } = useFormContext()
  const { currencyCode: localCurrencyCode } = useDesignSystem()
  const { i18n } = useLingui()

  const locale = i18n.locale as I18nLanguage

  const amountValue = watch(name)

  const fieldErrors = formState.errors
  const hasError = Boolean(fieldErrors[name])

  const { currency: currencySign, position: currencyPosition } =
    parseFormattedCurrencyNumber(
      formatCurrencyNumber(locale, {
        currency: currencyCode ?? localCurrencyCode,
      })(amountValue ?? 0)
    )

  const currencySignTemplate = (
    <CurrencySign disabled={disabled} position={currencyPosition}>
      {currencySign}
    </CurrencySign>
  )

  const startAdornment =
    currencyPosition === 'start' ? currencySignTemplate : null
  const endAdornment = currencyPosition === 'end' ? currencySignTemplate : null

  const id = nodeId ?? name

  return (
    <>
      {label !== undefined && (
        <InputLabel disabled={disabled} focused={focused} htmlFor={id}>
          {label}
        </InputLabel>
      )}
      <InputBox
        endAdornment={endAdornment}
        focused={focused}
        hasError={hasError}
        startAdornment={startAdornment}
      >
        <CurrencyInputBase
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          css={styles.input(textAlign)}
          data-testid="currency-input"
          disabled={disabled}
          id={id}
          max={max}
          min={min}
          name={name}
          readOnly={readOnly}
          rules={rules}
          onBlur={() => setFocused(false)}
          onFocus={() => setFocused(true)}
        />
      </InputBox>
      <FormHelperText
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      />
    </>
  )
}
